import { createLogger } from '@/lib/logger';
import { CartEvents } from './core';
import { CartEventBus } from './event-bus';

type FetchLike = typeof fetch;

/**
 * Options controlling which fetch lifecycle events are emitted.
 */
type FetchInterceptorOptions = {
  emitRequest: boolean;
  emitResponse: boolean;
};

const isCartUrl = (url: string) => /\/cart(.js|(\/.*?).js)/i.test(url);
const cartRoute = (path: string) => `${window.Shopify.routes.root}${path}`;

let patched = false;
let originalFetch: FetchLike | null = null;
let originalXHR: XMLHttpRequest | null = null;

/**
 * Patches `window.fetch` to observe Shopify cart mutation requests.
 *
 * Behavior:
 * - Only intercepts requests whose URL matches `/cart/*.js` and method is `POST`.
 * - Optionally emits `cart:fetch:request` and `cart:fetch:response` for debugging/telemetry.
 * - If a response to a cart mutation does NOT include header `x-source: mini-cart`,
 *   emits corresponding success events and always emits `cart:changed`.
 * - For `/cart/add.js`, because Shopify does not return the cart object, it performs
 *   an additional `GET /cart.js` and uses that as the payload for the final Changed event.
 */
export class CartFetchInterceptor {
  // Defaults to false because other apps are spamming cart requests
  private options: FetchInterceptorOptions = {
    emitRequest: false,
    emitResponse: false,
  };

  private logger = createLogger('cart-fetch-interceptor');

  /**
   * Create a fetch interceptor bound to an event bus.
   * @param bus Event bus used to emit `CartEvents`.
   * @param options Optional flags to emit request/response events.
   */
  constructor(
    private bus: CartEventBus,
    options?: FetchInterceptorOptions,
  ) {
    if (options) {
      this.options = { ...this.options, ...options };
    }
  }

  /**
   * Start intercepting by patching `window.fetch`.
   * Idempotent: calling multiple times only patches once.
   */
  start(): void {
    if (patched) return;
    const original: FetchLike = window.fetch.bind(window);
    originalFetch = original;

    const instanceBus = this.bus;
    const instanceOptions = this.options;
    const logger = this.logger;

    this.logger.info('Starting fetch interceptor', instanceOptions);

    const oldXHR = window.XMLHttpRequest;
    originalXHR = window.XMLHttpRequest.bind(window) as any;

    function newXHR() {
      var realXHR = new oldXHR();
      realXHR.addEventListener(
        'readystatechange',
        function () {
          if (realXHR.readyState == 4 && realXHR.status == 200) {
            if (realXHR.responseURL.includes('/cart/add.js')) {
              async function emitSuccess() {
                const cartRes = await original(cartRoute('cart.js'), { method: 'GET' });
                const cartData = await cartRes.json();

                logger.info(
                  `Emitting ${CartEvents.AddSuccess} for ${realXHR.responseURL}`,
                  cartData,
                );
                instanceBus.emit(CartEvents.AddSuccess, { payload: cartData, cart: cartData });
              }
              emitSuccess();
            }
          }
        },
        false,
      );
      return realXHR;
    }

    // @ts-ignore
    window.XMLHttpRequest = newXHR;

    window.fetch = async function patchedFetch(
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : (input as Request).url;
      const method = (init?.method || (input as Request)?.method || 'GET').toUpperCase();

      const clonedRequest = input instanceof Request ? input.clone() : null;
      let clonedRequestBody: any = null;

      const isCart = isCartUrl(url);

      // Only intercept cart mutation requests, except for clear.js
      if (!isCart || (method !== 'POST' && url.indexOf('/clear') === -1))
        return original(input as any, init as any);

      if (clonedRequest) clonedRequestBody = await clonedRequest.json();

      if (isCart && instanceOptions.emitRequest)
        instanceBus.emit(CartEvents.FetchRequest, { url, method, init });

      try {
        const res = await original(input as any, init as any);
        if (isCart && instanceOptions.emitResponse) {
          const cloned = res.clone();
          logger.info('Cloned response', cloned);
          let data: any = null;
          try {
            const ct = cloned.headers.get('content-type') || '';
            if (ct.includes('/json') || ct.includes('/javascript')) data = await cloned.json();
          } catch {
            logger.info('Failed to parse response as JSON', cloned);
          }
          instanceBus.emit(CartEvents.FetchResponse, { url, method, status: res.status, data });
        }

        // If the X-Source header is not present, emit events for this request based on the URL
        if (isCart && res.headers.get('x-source') !== 'mini-cart') {
          let cloned = res.clone();
          let data: any = null;
          try {
            const ct = cloned.headers.get('content-type') || '';
            if (ct.includes('/json') || ct.includes('/javascript')) data = await cloned.json();
          } catch {
            logger.info('Failed to parse response as JSON', cloned);
          }

          logger.info('Parsed response', data);

          logger.info('Emitting command events for cart request', url);
          if (url.includes('/cart/add.js')) {
            // Unfortunately Shopify doesn't return the cart on add.js requests, so we need to do another fetch to get it

            const cartRes = await original(cartRoute('cart.js'), { method: 'GET' });
            const cartData = await cartRes.json();

            logger.info(`Emitting ${CartEvents.AddSuccess} for ${url}`, data);
            instanceBus.emit(CartEvents.AddSuccess, { payload: data, cart: cartData });

            // Replace data with cartData for the Changed event
            data = cartData;
          } else if (url.includes('/cart/change.js')) {
            logger.info(`Emitting ${CartEvents.ChangeSuccess} for ${url}`, data);
            instanceBus.emit(CartEvents.ChangeSuccess, { payload: clonedRequestBody, cart: data });
          } else if (url.includes('/cart/update.js')) {
            logger.info(`Emitting ${CartEvents.UpdateSuccess} for ${url}`, data);
            instanceBus.emit(CartEvents.UpdateSuccess, {
              payload: clonedRequestBody,
              cart: data,
            });
          } else if (url.includes('/cart/clear.js')) {
            logger.info(`Emitting ${CartEvents.ClearSuccess} for ${url}`);
            instanceBus.emit(CartEvents.ClearSuccess);
          }

          // Always emit a Changed event on success
          if (cloned.ok) {
            instanceBus.emit(CartEvents.Changed, { cart: data });
          } else {
            instanceBus.emit(CartEvents.Error, {
              endpoint: url,
              method,
              error: data.message || 'Unknown error',
            });
          }
        }

        return res;
      } catch (error) {
        if (isCart) instanceBus.emit(CartEvents.FetchError, { url, method, error });
        throw error;
      }
    } as FetchLike;

    patched = true;
  }

  /**
   * Stop intercepting by restoring the original `window.fetch`.
   */
  stop(): void {
    if (patched && originalFetch && originalXHR) {
      window.fetch = originalFetch;
      patched = false;
      originalFetch = null;

      // @ts-ignore
      window.XMLHttpRequest = originalXHR;
    }
  }
}
